import axios from "axios";
import { siteConfig, storageKey } from "./siteConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });
const DEVICE_ID_KEY = storageKey("device_id");

function hashString(value) {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function cookieValue(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function setDeviceCookie(value) {
  if (typeof document === "undefined" || !value) return;
  document.cookie = `${DEVICE_ID_KEY}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
}

function randomDeviceId() {
  const cryptoApi = typeof crypto !== "undefined" ? crypto : null;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getDeviceId() {
  if (typeof window === "undefined") return "";
  let value = "";
  try {
    value = window.localStorage.getItem(DEVICE_ID_KEY) || "";
    if (!value) {
      value = cookieValue(DEVICE_ID_KEY) || randomDeviceId();
      window.localStorage.setItem(DEVICE_ID_KEY, value);
    }
  } catch {
    value = cookieValue(DEVICE_ID_KEY) || randomDeviceId();
  }
  setDeviceCookie(value);
  return value;
}

function getDeviceFingerprint() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "";
  const screenInfo = typeof screen === "undefined" ? {} : screen;
  const timezone = Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "";
  const parts = [
    navigator.userAgent,
    navigator.platform,
    navigator.language,
    Array.isArray(navigator.languages) ? navigator.languages.join(",") : "",
    timezone,
    new Date().getTimezoneOffset(),
    screenInfo.width,
    screenInfo.height,
    screenInfo.colorDepth,
    window.devicePixelRatio,
    navigator.hardwareConcurrency,
    navigator.deviceMemory,
    navigator.maxTouchPoints,
  ];
  return hashString(parts.join("|"));
}

function getCanvasSignature() {
  if (typeof document === "undefined") return "";
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.textBaseline = "top";
    ctx.font = "16px Arial";
    ctx.fillStyle = "#f3c13a";
    ctx.fillRect(0, 0, 240, 60);
    ctx.fillStyle = "#102015";
    ctx.fillText(`${siteConfig.name} device check 17`, 8, 8);
    ctx.strokeStyle = "#7c3aed";
    ctx.strokeRect(4, 4, 180, 42);
    return hashString(canvas.toDataURL());
  } catch {
    return "";
  }
}

function getWebglSignature() {
  if (typeof document === "undefined") return "";
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "";
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    return hashString([vendor, renderer].join("|"));
  } catch {
    return "";
  }
}

function getDeviceSignature() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "";
  const screenInfo = typeof screen === "undefined" ? {} : screen;
  const timezone = Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "";
  const parts = [
    navigator.platform,
    navigator.language,
    Array.isArray(navigator.languages) ? navigator.languages.join(",") : "",
    timezone,
    new Date().getTimezoneOffset(),
    screenInfo.width,
    screenInfo.height,
    screenInfo.availWidth,
    screenInfo.availHeight,
    screenInfo.colorDepth,
    window.devicePixelRatio,
    navigator.hardwareConcurrency,
    navigator.deviceMemory,
    navigator.maxTouchPoints,
    getCanvasSignature(),
    getWebglSignature(),
  ];
  return hashString(parts.join("|"));
}

function handleAdminAuthExpired(err) {
  if (typeof window === "undefined") return;
  if (err?.response?.status !== 401) return;
  const url = String(err?.config?.url || "");
  const path = window.location.pathname;
  const adminRequest =
    url.startsWith("/admin") ||
    (path.startsWith("/admin") && ["/auth/me", "/auth/reset-password"].includes(url));
  if (!adminRequest || url === "/auth/admin/login") return;
  localStorage.removeItem("aurum_admin_token");
  localStorage.removeItem("aurum_token");
  if (path !== "/admin") window.location.assign("/admin");
}

function handlePlayerAuthExpired(err) {
  if (typeof window === "undefined") return;
  if (err?.response?.status !== 401) return;
  const url = String(err?.config?.url || "");
  const path = window.location.pathname;
  if (path.startsWith("/admin") || url.startsWith("/admin") || url.startsWith(`${API_BASE}/admin`)) return;
  localStorage.removeItem("aurum_token");
  window.dispatchEvent(new CustomEvent("sophia:auth-expired"));
}

api.interceptors.request.use((config) => {
  const url = String(config.url || "");
  const adminContext =
    url.startsWith("/admin") ||
    url.startsWith(`${API_BASE}/admin`) ||
    (typeof window !== "undefined" && window.location.pathname.startsWith("/admin"));
  const token = adminContext
    ? localStorage.getItem("aurum_admin_token") || localStorage.getItem("aurum_token")
    : localStorage.getItem("aurum_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const deviceId = getDeviceId();
  const fingerprint = getDeviceFingerprint();
  const signature = getDeviceSignature();
  if (deviceId) config.headers["X-Device-Id"] = deviceId;
  if (fingerprint) config.headers["X-Device-Fingerprint"] = fingerprint;
  if (signature) config.headers["X-Device-Signature"] = signature;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (err) => {
    handleAdminAuthExpired(err);
    handlePlayerAuthExpired(err);
    return Promise.reject(err);
  },
);

export function isTemporaryProviderError(err) {
  const fallbackText = [
    err?.response?.data,
    err?.response?.data?.detail,
    err?.message,
  ].map((value) => (typeof value === "string" ? value : value ? JSON.stringify(value) : "")).join(" ");
  const lower = fallbackText.toLowerCase();
  return (
    lower.includes("origin web server returned") ||
    lower.includes("temporary cloudflare") ||
    lower.includes("temporary origin error") ||
    lower.includes("provider server returned a temporary")
  );
}

export function formatApiError(err, options = {}) {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 401) {
    const url = String(err?.config?.url || "");
    if (url.includes("/auth/login") || url.includes("/auth/admin/login")) {
      return typeof d === "string" && d ? d : "Invalid email or password";
    }
    if (typeof d === "string" && d && d !== "Not authenticated") {
      return d;
    }
    return "Please sign in again.";
  }
  const rawText = [
    err?.response?.data,
    err?.response?.data?.detail,
    err?.message,
  ].map((value) => (typeof value === "string" ? value : value ? JSON.stringify(value) : "")).join(" ").toLowerCase();
  if (isTemporaryProviderError(err)) {
    if (options.audience === "player") {
      return "Game provider is temporarily unavailable. Please try again in a moment.";
    }
    return "Provider server is temporarily unavailable. Retry after a moment; if credentials are correct, save without live test and test again later.";
  }
  const stillInGameText = (
    rawText.includes("player is still in the game") ||
    rawText.includes("still in the game") ||
    rawText.includes("return to the game lobby") ||
    rawText.includes("exit to the lobby") ||
    rawText.includes("inside the game")
  );
  if (options.audience === "player" && stillInGameText) {
    return "The game provider rejected this because you are still inside the game. Please exit to the game lobby or fully close the game app, tap Refresh balance, then try again.";
  }
  const safeBalanceText = (
    rawText.includes("safe balance") ||
    rawText.includes("safe_balance") ||
    rawText.includes("bank balance") ||
    rawText.includes("vault balance")
  );
  if (options.audience === "player" && safeBalanceText) {
    return "Safe balance must be out first. Move Safe/Bank balance back to the main game balance, tap Refresh balance, then redeem again.";
  }
  const balanceRefreshText = (
    rawText.includes("could not verify current game balance") ||
    rawText.includes("could not read the current game balance") ||
    rawText.includes("could not be verified on the provider balance") ||
    rawText.includes("provider response was unclear") ||
    rawText.includes("game connection is being refreshed") ||
    rawText.includes("live balance is refreshing") ||
    rawText.includes("live sync retrying") ||
    rawText.includes("temporarily_unavailable")
  );
  if (options.audience === "player" && balanceRefreshText) {
    return "Live game balance is refreshing. Try again in a moment.";
  }
  const automationCreateText = (
    rawText.includes("vblink create account failed") ||
    rawText.includes("not allowed to create") ||
    rawText.includes("cannot create") ||
    rawText.includes("manual setup") ||
    rawText.includes("needs admin setup") ||
    rawText.includes("代理不能创建") ||
    rawText.includes("不能创建")
  );
  if (options.audience === "player" && automationCreateText) {
    return "Game automation could not create this account. Please try again shortly.";
  }
  if (
    options.audience === "player" &&
    rawText.includes("pending admin review")
  ) {
    return "This request was sent to admin review. Support will verify it and update your wallet if approved.";
  }
  if (
    options.audience === "player" &&
    (
      rawText.includes("backend login failed") ||
      rawText.includes("provider session") ||
      rawText.includes("provider token") ||
      rawText.includes("captcha") ||
      rawText.includes("verification code") ||
      rawText.includes("backend setup") ||
      rawText.includes("provider login") ||
      rawText.includes("automation account repair failed") ||
      rawText.includes("account repair failed") ||
      rawText.includes("automation setup needs attention") ||
      rawText.includes("no wallet credit/debit was approved")
    )
  ) {
    return "Game automation is reconnecting. Please try again shortly.";
  }
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (d?.msg) return d.msg;
  if (d?.message && d?.response?.error) return `${d.message}: ${d.response.error}`;
  if (d?.message && d?.response?.message) return `${d.message}: ${d.response.message}`;
  if (d?.message) return d.message;
  if (d?.response?.error) {
    const details = d.response.details ? ` ${JSON.stringify(d.response.details)}` : "";
    return `${d.response.error}${details}`;
  }
  if (d?.error) {
    const details = d.details ? ` ${JSON.stringify(d.details)}` : "";
    return `${d.error}${details}`;
  }
  return JSON.stringify(d);
}

export default api;
